// src/prototype/components/execute/ExecPmoHandoffTab.tsx
// ============================================================
// Execute hub · Use Case Execution tab (E6, Oct 21): the handoff to the health system's PMO.
// Handoff record and named roles, charter summary, milestones, requirements, risks, RACI,
// the measurement plan (the "outcome" half of the decision -> outcome record) and the
// downloadable kit.
// ============================================================
import React from 'react';
import { ClipboardList, Users, Calendar, Wrench, Shield, Target, Download, FileText, Check } from 'lucide-react';
import { Collapse } from '../Collapse';
import { MilestoneRow } from '../MilestoneRow';
import { ActivationChecklist } from '../ActivationChecklist';
import { TeamList } from '../TeamList';
import { SEVERITY_DOT } from '../../theme/decisionTokens';
import type { DecisionBrief } from '../../data/decisionBrief';
import type { InitiativeRecord, ExecMilestone, ChecklistItem } from '../../data/executionInitiatives';
import type { MilestoneStatus } from '../../theme/decisionTokens';
import type { RequirementVM } from '../../data/adapters';
import type { HandoffState, Note } from '../../hooks/useExecuteHubState';
import { NotesThread } from './ExecApprovalTab';
import {
  RACI, RACI_ROLES, VALUE_STEPS_CITATION, getCharter, getMeasurementPlan, getHandoffDefaults,
  downloadHandoffWorkbook, kickoffOutline, downloadText, type HandoffKitInput,
} from '../../data/execute/pmoHandoff';

export interface ExecPmoHandoffTabProps {
  brief: DecisionBrief;
  record: InitiativeRecord;
  clientName: string;
  scoresAsOf: string;
  milestones: ExecMilestone[];
  checklist: ChecklistItem[];
  requirements: RequirementVM[];
  handoff: HandoffState;
  onHandoff: (patch: Partial<HandoffState>) => void;
  onSetMilestoneStatus: (title: string, status: MilestoneStatus, pick?: number) => void;
  onToggleChecklist: (label: string) => void;
  updatedLabelFor: (title: string) => string | undefined;
  onGoApproval: () => void;
  milestoneNotes?: Note[];
  onAddMilestoneNote?: (title: string, text: string) => void;
}

const REQ_LABEL: Record<string, string> = { data: 'Data', technology: 'Technology', people: 'People', governance: 'Governance' };
const input = 'text-xs text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1.5 bg-white w-full focus:outline-none focus:ring-1 focus:ring-[#18365E]/20';

export const ExecPmoHandoffTab: React.FC<ExecPmoHandoffTabProps> = ({
  brief, record, clientName, scoresAsOf, milestones, checklist, requirements, handoff,
  onHandoff, onSetMilestoneStatus, onToggleChecklist, updatedLabelFor, onGoApproval, milestoneNotes = [], onAddMilestoneNote,
}) => {
  const charter = getCharter(brief, record.sponsor);
  const plan = getMeasurementPlan(brief);
  // Pre-filled handoff for the demo; anything the user enters overrides the default.
  const defaults = getHandoffDefaults(record);
  const eff = {
    pmoOwner: handoff.pmoOwner || defaults.pmoOwner,
    handoffDate: handoff.handoffDate || defaults.handoffDate,
    status: handoff.status !== 'Not started' ? handoff.status : defaults.status,
    roles: Object.fromEntries(Object.keys(defaults.roles).map((k) => [k, handoff.roles[k as keyof HandoffState['roles']] || defaults.roles[k as keyof HandoffState['roles']]])) as HandoffState['roles'],
  };
  const kit: HandoffKitInput = {
    brief, clientName, charter, milestones, risks: record.risks, requirements, team: record.team,
    metrics: plan.metrics, handoff: eff, scoresAsOf,
  };
  const slug = brief.title.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const reqTypes = ['data', 'technology', 'people', 'governance'] as const;
  const fmtD = (ymd: string) => (ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'date not set');
  const doneMs = milestones.filter((m) => m.status === 'Complete').length;
  const blockedMs = milestones.filter((m) => m.blocker).length;
  const openRisks = record.risks.filter((r) => r.status === 'Open').length;
  const checklistDone = checklist.filter((c) => c.done).length;
  const kitBtn = 'text-xs font-semibold rounded-lg px-3 py-1.5 flex items-center gap-1.5 whitespace-nowrap';

  return (
    <div className="space-y-4">
      {/* Handoff kit: one line. */}
      <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-2.5 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-[#18365E] uppercase tracking-wide flex items-center gap-1.5 mr-1"><Download className="w-3.5 h-3.5" />Handoff kit</span>
        <button onClick={() => downloadHandoffWorkbook(kit)} title="Charter, RACI, milestone plan, risk log, requirements, measurement plan and team"
          className={`${kitBtn} text-white bg-[#18365E] hover:bg-[#122a4a]`}>
          <Download className="w-3.5 h-3.5" /> PMO workbook (XLSX)
        </button>
        <button onClick={() => downloadText(`${slug}_Kickoff_Outline.txt`, kickoffOutline(kit))} className={`${kitBtn} text-[#18365E] border border-[#18365E]/30 hover:bg-[#F5F7FA]`}>
          <FileText className="w-3.5 h-3.5" /> Kickoff outline
        </button>
        <button onClick={onGoApproval} className={`${kitBtn} text-[#18365E] border border-[#18365E]/30 hover:bg-[#F5F7FA]`}>
          <FileText className="w-3.5 h-3.5" /> Approval artifacts
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="space-y-3">
          <Collapse icon={<ClipboardList className="w-3.5 h-3.5" />} title="PMO handoff"
            meta={`${eff.status} · ${eff.pmoOwner} · ${eff.status === 'Not started' ? 'planned after approval' : fmtD(eff.handoffDate)}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide">PMO owner</span>
                <input className={input} value={eff.pmoOwner} onChange={(e) => onHandoff({ pmoOwner: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide">Handoff date</span>
                  <input className={input} type="date" value={eff.handoffDate} onChange={(e) => onHandoff({ handoffDate: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide">Status</span>
                  <select className={input} value={eff.status} onChange={(e) => onHandoff({ status: e.target.value as HandoffState['status'] })}>
                    <option>Not started</option>
                    <option>Handed off</option>
                    <option>Accepted by PMO</option>
                  </select>
                </label>
              </div>
              {(Object.keys(eff.roles) as (keyof HandoffState['roles'])[]).map((role) => (
                <label key={role} className="flex flex-col gap-1">
                  <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide">{role}</span>
                  <input className={input} value={eff.roles[role]} placeholder="Name" onChange={(e) => onHandoff({ roles: { ...handoff.roles, [role]: e.target.value } })} />
                </label>
              ))}
            </div>
          </Collapse>

          <Collapse icon={<FileText className="w-3.5 h-3.5" />} title="Charter summary" meta={`Sponsor ${charter.sponsor} · ${charter.scope.length} items in scope`}>
            <dl className="space-y-2.5 text-xs">
              <div><dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">Problem</dt><dd className="text-[#4b5563] mt-0.5 leading-relaxed">{charter.problem}</dd></div>
              <div><dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">In scope</dt>
                <dd className="mt-0.5"><ul className="space-y-1">{charter.scope.map((x) => <li key={x} className="flex gap-1.5 text-[#4b5563]"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />{x}</li>)}</ul></dd></div>
              <div className="grid grid-cols-2 gap-3">
                <div><dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">Out of scope</dt><dd className="text-[#4b5563] mt-0.5">{charter.outOfScope}</dd></div>
                <div><dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">Executive sponsor</dt><dd className="font-bold text-[#18365E] mt-0.5">{charter.sponsor}</dd></div>
              </div>
            </dl>
          </Collapse>

          <Collapse icon={<Calendar className="w-3.5 h-3.5" />} title="Milestones" defaultOpen
            meta={`${doneMs} of ${milestones.length} complete${blockedMs ? ` · ${blockedMs} waiting on a dependency` : ''}`}>
            <div className="space-y-2.5">
              {milestones.map((m) => {
                const mNotes = milestoneNotes.filter((n) => n.target.kind === 'milestone' && n.target.title === m.title);
                return (
                  <div key={m.title}>
                    <MilestoneRow milestone={m} onSetStatus={(status, pick) => onSetMilestoneStatus(m.title, status, pick)} updatedLabel={updatedLabelFor(m.title)} />
                    {onAddMilestoneNote && (
                      <details className="ml-3 mt-1">
                        <summary className="text-[11px] font-semibold text-[#717182] hover:text-[#18365E] cursor-pointer">Notes{mNotes.length ? ` (${mNotes.length})` : ''}</summary>
                        <div className="mt-1.5"><NotesThread notes={mNotes} onAdd={(text) => onAddMilestoneNote(m.title, text)} placeholder={`Add a note on ${m.title}`} /></div>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          </Collapse>

          <Collapse icon={<Target className="w-3.5 h-3.5" />} title="Measurement plan" meta={`${plan.metrics.length} metrics · Value STEPS tagged${plan.reviewed ? '' : ' · starter plan'}`}>
            {!plan.reviewed && <p className="text-[11px] text-[#717182] mb-2">Starter plan from the evaluation. Confirm metrics at kickoff.</p>}
            <div className="overflow-x-auto rounded-lg border border-[#e5e7eb]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#F5F7FA] text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
                    <th className="text-left px-3 py-2">Metric</th>
                    <th className="text-left px-3 py-2">Value STEPS</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">Baseline definition</th>
                    <th className="text-left px-3 py-2">Baseline</th>
                    <th className="text-left px-3 py-2">Target</th>
                    <th className="text-left px-3 py-2 hidden lg:table-cell">Data source</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.metrics.map((m) => (
                    <tr key={m.metric} className="border-t border-[#f1f5f9] align-top">
                      <td className="px-3 py-2 font-semibold text-[#18365E]">{m.metric}</td>
                      <td className="px-3 py-2"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#EEF2F7] text-[#18365E]">{m.valueStep}</span></td>
                      <td className="px-3 py-2 text-[#4b5563] hidden md:table-cell">{m.baselineDefinition}</td>
                      <td className="px-3 py-2 text-[#717182]">{m.baselineValue}</td>
                      <td className="px-3 py-2 text-[#717182]">{m.target}</td>
                      <td className="px-3 py-2 text-[#4b5563] hidden lg:table-cell">{m.dataSource}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-[#717182] mt-2">{plan.metrics[0]?.measurementDates}. {VALUE_STEPS_CITATION}</p>
          </Collapse>

          <Collapse icon={<Wrench className="w-3.5 h-3.5" />} title="Implementation requirements" meta={requirements.length ? `${requirements.length} listed` : 'to be documented at kickoff'}>
            {requirements.length === 0 ? (
              <p className="text-xs text-[#717182]">To be documented at kickoff across data, technology, people and governance.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {reqTypes.map((t) => {
                  const items = requirements.filter((r) => r.type === t);
                  if (!items.length) return null;
                  return (
                    <div key={t}>
                      <p className="text-[10px] font-bold text-[#717182] uppercase tracking-wide mb-1">{REQ_LABEL[t]}</p>
                      <ul className="space-y-1.5">
                        {items.map((r) => <li key={r.name} className="text-xs"><b className="text-[#18365E]">{r.name}</b> <span className="text-[#4b5563]">{r.details}</span></li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </Collapse>

          <Collapse icon={<Shield className="w-3.5 h-3.5" />} title="Risk register" meta={`${openRisks} open of ${record.risks.length}`}>
            <div className="space-y-3">
              {record.risks.map((risk) => (
                <div key={risk.title} className="bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] p-3.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${SEVERITY_DOT[risk.severity] ?? 'bg-slate-400'}`} />
                    <span className="text-xs font-bold text-[#18365E]">{risk.title}</span>
                    <span className="text-[10px] font-semibold text-[#717182]">{risk.severity} · {risk.likelihood} likelihood</span>
                    <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-white border border-[#e5e7eb] text-[#717182]">{risk.status}</span>
                  </div>
                  <p className="text-xs text-[#4b5563] leading-relaxed mt-1.5">{risk.description}</p>
                  <p className="text-xs text-[#4b5563] leading-relaxed mt-1"><b className="text-[#18365E]">Mitigation:</b> {risk.mitigation} <span className="text-[#717182]">· {risk.owner}</span></p>
                </div>
              ))}
            </div>
          </Collapse>

          <Collapse icon={<Users className="w-3.5 h-3.5" />} title="RACI" meta={`starter matrix · ${RACI.length} activities`}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] font-semibold text-[#717182]">
                    <th className="text-left px-2 py-1.5">Activity</th>
                    {RACI_ROLES.map((r) => <th key={r} className="px-2 py-1.5 text-center">{r}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {RACI.map((row) => (
                    <tr key={row.activity} className="border-t border-[#f1f5f9]">
                      <td className="px-2 py-1.5 font-semibold text-[#18365E]">{row.activity}</td>
                      {row.codes.map((c, i) => <td key={i} className={`px-2 py-1.5 text-center font-bold ${c === 'A' ? 'text-[#FF6E4C]' : c === 'R' ? 'text-[#18365E]' : 'text-[#717182]'}`}>{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-[#717182] mt-2">R responsible · A accountable · C consulted · I informed. Edit at kickoff.</p>
            </div>
          </Collapse>
        </div>

        <div className="space-y-3">
          <Collapse icon={<Check className="w-3.5 h-3.5" />} title="Activation checklist" meta={`${checklistDone} of ${checklist.length}`} defaultOpen>
            <ActivationChecklist items={checklist} onToggle={onToggleChecklist} />
          </Collapse>
          <Collapse icon={<Users className="w-3.5 h-3.5" />} title="Team" meta={`${record.team.length} named`} defaultOpen>
            <TeamList team={record.team} />
          </Collapse>
        </div>
      </div>
    </div>
  );
};

export default ExecPmoHandoffTab;
